import * as authActions from './auth.actions';
import * as fromCore from './index';

import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthService } from '../services/auth.service';
import { Observable, of } from 'rxjs';
import { catchError, tap, withLatestFrom } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { LoggerService } from '@services/logger.service';
import { Router } from '@angular/router';
import { TabSessionService } from '../services/tab-session.service';
import { ToastService } from '@services/toast.service';

// ! The auth HTTP flows (login/logout/remoteLogin) live in `AuthFlowService`;
// ! this file is client-state orchestration reacting to the auth broadcasts.

type LoginSuccessAction = ReturnType<typeof authActions.loginSuccess>;
@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
    private store: Store,
    private logger: LoggerService,
    private tabSessionService: TabSessionService,
    private queryClient: QueryClient,
  ) {}

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loginSuccess, authActions.remoteLoginSuccess),
        withLatestFrom(this.store.select(fromCore.selectRedirectUrl)),
        tap(
          ([action, redirectUrl]: [LoginSuccessAction, string | undefined]) => {
            this.authService.setCredentials(
              {
                token: action.response.access_token,
                expiresAt:
                  Number(action.response.access_token_expires_at) || undefined,
              },
              action.remember,
            );

            // Seed the per-tab company context, but never clobber a tab that
            // already switched company.
            const activeCompanyId = action.response.user?.active_company_id;
            if (
              activeCompanyId &&
              !this.tabSessionService.getActiveCompanyId()
            ) {
              this.tabSessionService.setActiveCompanyId(activeCompanyId);
            }

            if (redirectUrl) {
              this.logger.debug('Redirecting to', redirectUrl);
              this.router.navigateByUrl(redirectUrl);
            }
          },
        ),
      ),
    { dispatch: false },
  );

  authCheck$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.authCheck),
        // NOTE: no take(1) here - it completed the whole effect after the first
        // authCheck, so later dispatches were silently ignored
        withLatestFrom(this.store.pipe(select(fromCore.selectIsAuthenticated))),
        tap(([action, isAuthenticated]) => {
          // The /auth routes are the public pages (login, forgot/reset/create
          // password, validate-account, remote login) - never redirect off them.
          const onAuthRoute =
            this.router.url.startsWith('/auth') ||
            window.location.pathname.startsWith('/auth');
          if (!isAuthenticated && !onAuthRoute) {
            this.router.navigate(['/auth/login']).catch((error) => {
              console.error('Navigation error in authCheck$ effect', error);
            });
          }
        }),
        catchError((error) => {
          console.error('Error in authCheck$ effect', error);
          return of({ type: '[Auth] Auth Check Error', error });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Every cached server response belongs to the session and company that fetched
   * it, so the whole query cache is dropped when either changes - the same
   * trigger pair the hand-rolled `clearCache()` services listen on.
   */
  clearQueryCacheOnSessionChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.logout, authActions.switchUserCompany),
        tap(() => this.queryClient.clear()),
      ),
    { dispatch: false },
  );

  remoteLoginFailUi$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.remoteLoginFail),
        tap(({ error }) => {
          this.toastService.showError(error || 'Login failed');
          this.router.navigate(['/auth/login']);
        }),
      ),
    { dispatch: false },
  );

  /**
   * `authInitFactory` dispatches `loginSuccess` from an `APP_INITIALIZER`, before any
   * observer exists, so do not add query invalidations here - they would have nothing
   * to match. The lists are fetched by whoever mounts first instead.
   */
  persistDarkModeOnLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loginSuccess, authActions.remoteLoginSuccess),
        tap(({ response }) => {
          localStorage.setItem(
            'darkMode',
            JSON.stringify(response.user.dark_mode),
          );
        }),
      ),
    { dispatch: false },
  );

  switchUserCompanySuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.switchUserCompanySuccess),
        withLatestFrom(
          this.store.select(fromCore.selectUserAccessLevel),
          this.store.select(fromCore.selectCoreAvailableModules),
          this.store.select(fromCore.selectCoreAppSection),
        ),
        tap(([action, userAccessLevel, availableModules, appSection]) => {
          const currentModule = availableModules.find(
            (module) => module.name.toLowerCase() === appSection,
          );

          if (currentModule && userAccessLevel >= currentModule.accessLevel) {
            this.router.navigate([currentModule.url]);
          } else {
            this.router.navigate(['/analytics']);
          }
        }),
      ),
    { dispatch: false },
  );
}
